import type {
  CommandGroup,
  CommandItem,
} from "@replo/design-system/utils/commands";

import * as React from "react";

import omit from "lodash-es/omit";
import { createShallowStore } from "replo-utils/react/zustand";

export type DynamicCommandItem = CommandItem & {
  group: CommandGroup["id"];
};

type CommandMenuStore = {
  items: Record<string, DynamicCommandItem>;
  addItem: (key: string, item: DynamicCommandItem) => void;
  removeItem: (key: string) => void;
};

const useCommandMenuStore = createShallowStore<CommandMenuStore>((set) => ({
  items: {},
  addItem: (key, item) => {
    return set((state) => ({ items: { ...state.items, [key]: item } }));
  },
  removeItem: (key) => set((state) => ({ items: omit(state.items, key) })),
}));

export function useAllDynamicCommandMenuItems(): Record<
  string,
  DynamicCommandItem
> {
  return useCommandMenuStore((state) => state.items);
}

export function useDynamicCommandMenuItem(
  key: string,
  item?: DynamicCommandItem | null,
) {
  const addItem = useCommandMenuStore((state) => state.addItem);
  const removeItem = useCommandMenuStore((state) => state.removeItem);

  React.useEffect(() => {
    if (!item) {
      removeItem(key);
      return;
    }

    addItem(key, item);

    return () => {
      removeItem(key);
    };
  }, [key, item, addItem, removeItem]);
}

/**
 * Hook for registering multiple command menu items at once.
 * Items are automatically added on mount and removed on unmount.
 */
export function useDynamicCommandMenuItems(
  items: Record<string, DynamicCommandItem>,
) {
  const addItem = useCommandMenuStore((state) => state.addItem);
  const removeItem = useCommandMenuStore((state) => state.removeItem);

  React.useEffect(() => {
    // Add all items
    Object.entries(items).forEach(([key, item]) => {
      addItem(key, item);
    });

    // Cleanup: remove all items when unmounting or items change
    return () => {
      Object.keys(items).forEach((key) => {
        removeItem(key);
      });
    };
  }, [items, addItem, removeItem]);
}
